import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative } from '@/lib/platform';
import { useTaskStore } from '@/stores/taskStore';
import { useRoutineStore } from '@/stores/routineStore';
import { useHabitStore } from '@/stores/habitStore';
import { useUIStore } from '@/stores/uiStore';
// Quiet hours filtering disabled until Settings UI for configuring hours is built
// import { isInQuietHours } from './quietHours';

interface NotifCandidate {
  id: number;
  title: string;
  body: string;
  fireAt: Date;
  extra: { type: 'task' | 'routine' | 'habit'; id: string };
}

function collectCandidates(): NotifCandidate[] {
  const now = new Date();
  const horizon = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const candidates: NotifCandidate[] = [];
  const ui = useUIStore.getState();

  // Tasks
  if (ui.notifTasks) {
    const { tasks } = useTaskStore.getState();
    for (const task of tasks) {
      if (!task.scheduledStart) continue;
      if (task.status === 'done' || task.status === 'skipped') continue;
      const fireAt = new Date(task.scheduledStart.getTime() - 5 * 60 * 1000);
      if (fireAt <= now || fireAt > horizon) continue;
      candidates.push({
        id: hashId(`task-${task.id}`),
        title: task.title,
        body: 'Starting in five minutes',
        fireAt,
        extra: { type: 'task', id: task.id },
      });
    }
  }

  // Routines
  if (ui.notifRoutines) {
    const { routines } = useRoutineStore.getState();
    const today = now.getDay();
    for (const routine of routines) {
      if (!routine.days.includes(today as 0 | 1 | 2 | 3 | 4 | 5 | 6)) continue;
      if (!routine.startTimeHHMM) continue;
      const [h, m] = routine.startTimeHHMM.split(':').map(Number);
      const fireAt = new Date(now);
      fireAt.setHours(h, m, 0, 0);
      fireAt.setTime(fireAt.getTime() - 5 * 60 * 1000);
      if (fireAt <= now || fireAt > horizon) continue;
      candidates.push({
        id: hashId(`routine-${routine.id}`),
        title: routine.name,
        body: 'Routine starts in five minutes',
        fireAt,
        extra: { type: 'routine', id: routine.id },
      });
    }
  }

  // Habits
  if (ui.notifHabits) {
    const { habits } = useHabitStore.getState();
    for (const habit of habits) {
      if (!habit.scheduledTime) continue;
      const [h, m] = habit.scheduledTime.split(':').map(Number);
      const fireAt = new Date(now);
      fireAt.setHours(h, m, 0, 0);
      fireAt.setTime(fireAt.getTime() - 5 * 60 * 1000);
      if (fireAt <= now || fireAt > horizon) continue;
      candidates.push({
        id: hashId(`habit-${habit.id}`),
        title: habit.name,
        body: 'Time for your habit in five minutes',
        fireAt,
        extra: { type: 'habit', id: habit.id },
      });
    }
  }

  return candidates;
}

function hashId(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export async function scheduleNotifications(): Promise<void> {
  if (!isNative()) return;
  try {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') return;

    const candidates = collectCandidates();
    const pending = await LocalNotifications.getPending();
    const pendingIds = new Set(pending.notifications.map((n) => n.id));
    const candidateIds = new Set(candidates.map((c) => c.id));

    // Cancel stale
    const toCancel = pending.notifications.filter((n) => !candidateIds.has(n.id));
    if (toCancel.length > 0) {
      await LocalNotifications.cancel({ notifications: toCancel.map((n) => ({ id: n.id })) });
    }

    // Schedule new
    const toSchedule = candidates.filter((c) => !pendingIds.has(c.id));
    if (toSchedule.length > 0) {
      await LocalNotifications.schedule({
        notifications: toSchedule.map((c) => ({
          id: c.id,
          title: c.title,
          body: c.body,
          schedule: { at: c.fireAt },
          actionTypeId: 'TASK_REMINDER',
          channelId: 'brainday-reminders',
          extra: c.extra,
        })),
      });
    }
  } catch {
    // non-fatal
  }
}
